import { Link as RouterLink } from "react-router-dom";
import { Link, NavLink } from "@/components/LocaleLink";
import { useI18n } from "@/i18n/I18nProvider";
import { LOCALES, LOCALE_META } from "@/i18n/config";
import { useTheme } from "@/components/ThemeProvider";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { LogOut, Settings } from "lucide-react";

const Header = () => {
  const { resolvedTheme, setTheme } = useTheme();
  const { m, locale, switchTo } = useI18n();
  const { user, signOut } = useAuth();
  const { isAdmin } = useUserRole();

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <nav className="flex items-center justify-between h-20">
          <Link to="/" className="font-sans font-semibold text-[2rem] tracking-[-0.02em]">
            BioNexus
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <NavLink to="/materials" className={({ isActive }) => `nav-link font-mono text-xs uppercase tracking-[0.1em] transition-colors ${isActive ? "text-foreground active" : "text-muted-foreground hover:text-foreground"}`}>
              {m.nav.materials}
            </NavLink>
            <NavLink to="/applications" className={({ isActive }) => `nav-link font-mono text-xs uppercase tracking-[0.1em] transition-colors ${isActive ? "text-foreground active" : "text-muted-foreground hover:text-foreground"}`}>
              {m.nav.applications}
            </NavLink>
            <NavLink to="/about" className={({ isActive }) => `nav-link font-mono text-xs uppercase tracking-[0.1em] transition-colors ${isActive ? "text-foreground active" : "text-muted-foreground hover:text-foreground"}`}>
              {m.nav.about}
            </NavLink>
            {isAdmin && (
              <RouterLink to="/admin" className="font-mono text-xs uppercase tracking-[0.1em] text-primary hover:text-primary/80 transition-colors flex items-center gap-1">
                <Settings className="w-3 h-3" />
                {m.nav.admin}
              </RouterLink>
            )}
          </div>

          <div className="flex items-center gap-4">
            <div role="group" aria-label={m.nav.language} className="flex items-center gap-2.5">
              {LOCALES.map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => switchTo(l)}
                  aria-pressed={locale === l}
                  lang={LOCALE_META[l].htmlLang}
                  title={LOCALE_META[l].name}
                  className={`text-xs tracking-[0.06em] transition-colors ${
                    locale === l ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {LOCALE_META[l].label}
                </button>
              ))}
            </div>
            <span aria-hidden className="h-3 w-px bg-border" />
            <button
              onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
              className="font-mono text-xs uppercase tracking-[0.1em] text-muted-foreground hover:text-primary transition-colors"
            >
              {resolvedTheme === "dark" ? m.nav.light : m.nav.dark}
            </button>
            
            {user && (
              <button
                onClick={handleSignOut}
                className="font-mono text-xs uppercase tracking-[0.1em] text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"
                title={m.nav.signOut}
              >
                <LogOut className="w-3 h-3" />
                <span className="hidden sm:inline">{m.nav.signOut}</span>
              </button>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
